import React, { useEffect, useState } from 'react';

import { Alert, Box, Button, Stack } from '@mui/material';

import { isAccommodationPackage } from '~/components/Offers/Detail/typeNarrowingUtils';

import { ROLE_ADMIN_USER, ROLE_LUX_LOYALTY_ADMIN } from '~/consts/roles';

import { useFlashHotelCatalog } from '~/hooks/apis/rewards';
import useCurrentUser from '~/hooks/useCurrentUser';

import type { SocieteRewardCopayPrice, SocieteRewardPatchResult } from '~/services/LuxLoyalty';

import canShowTab from '~/utils/canShowTab';

import SocieteRewardOverrideDialog from './SocieteRewardOverrideDialog';
import SocieteRewardSummary from './SocieteRewardSummary';
import { flashConfigState } from './validateFlashConfig';

interface Props {
  /** Salesforce external offer ID the package belongs to. */
  offerSfId: string;
  /** The package to show/manage the Société reward for. */
  pkg: App.Package;
}

/**
 * Maps a package's `prices` rows to the per-currency co-pay shape `SocieteRewardSummary` and
 * `SocieteRewardOverrideDialog` expect. Only `AccommodationPackage` carries `prices` — tour
 * packages (and, defensively, any package shape without prices) map to an empty list.
 */
function mapCopayPrices(pkg: App.Package): Array<SocieteRewardCopayPrice> {
  if (!isAccommodationPackage(pkg)) {
    return [];
  }
  return (pkg.prices ?? []).map((price) => ({
    currency_code: price.currency_code,
    societe_reward_copay_base: price.societe_reward_copay_base ?? null,
    societe_reward_copay_extra_night: price.societe_reward_copay_extra_night ?? null,
    // Passed through verbatim (NOT `?? null`) so an absent field (`undefined` — the API response
    // predates this field) stays distinguishable from an explicit `null` (confirmed zero copay).
    // `formulaRewardInputs` relies on that distinction to decide whether the override dialog's
    // formula margin/VPP can be computed at all, or should render "n/a".
    societe_reward_computed_copay_base: price.societe_reward_computed_copay_base,
    societe_reward_computed_copay_extra_night: price.societe_reward_computed_copay_extra_night,
  }));
}

/**
 * Admin row for one package's Société flash-hotel reward on the offer Detail page: renders the
 * computed/overridden reward summary, and — for admins with the right role — a "Manage" button
 * that opens the override dialog.
 *
 * The Manage flow only renders once the package has a computed reward: "Not priced yet" packages
 * (the pricing job hasn't reached them) get the summary alone, since there's nothing to override
 * yet. Applying a change updates local state directly rather than refetching — the offer Detail
 * page loads its packages via a legacy fetch that this component doesn't re-run.
 *
 * `pkg` is re-seeded into local state whenever its `societe_reward`/`prices` identity changes
 * (e.g. PackagesPane rebuilding its packages array after a parent refetch, such as another pane's
 * `onRefresh`). Since `pkg` always carries current server state — which already reflects any
 * applied override — a new `pkg` always wins over whatever this component is currently showing,
 * so this never clobbers a fresher `onApplied` result with a stale one: `pkg` only changes on an
 * actual refetch, never as a side effect of this component's own dialog submissions.
 */
export default function PackageSocieteRewardRow({ offerSfId, pkg }: Props) {
  const { user } = useCurrentUser();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reward, setReward] = useState<SocieteRewardPatchResult | null>(null);

  // Eligibility thresholds shown here (margin warning, VPP window) track the Flash Config tab's
  // live values. There are no local defaults: when the config can't be loaded the warnings are
  // switched off and an error says why.
  const configState = flashConfigState(useFlashHotelCatalog());
  const flashConfig = configState.status === 'ready' ? configState.config : null;

  // Only AccommodationPackage carries `prices` (App.Package is a union with TourPackage).
  const accommodationPrices = isAccommodationPackage(pkg) ? pkg.prices : undefined;

  useEffect(() => {
    setReward(pkg.societe_reward ? { societe_reward: pkg.societe_reward, prices: mapCopayPrices(pkg) } : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pkg.societe_reward, accommodationPrices]);

  const canManage = canShowTab(user, [ROLE_ADMIN_USER, ROLE_LUX_LOYALTY_ADMIN]);
  // Selling currencies come straight off the package's own prices — kept live for the dialog
  // independent of whatever reward snapshot is currently seeded.
  const sellingCurrencies = accommodationPrices?.map((price) => price.currency_code) ?? [];
  // Classic rewards margin / VPP previews need the package's AUD cost-of-goods-sold and LuxPlus+
  // price; fall back to null (renders as "n/a") when the package doesn't sell in AUD or the field
  // isn't populated.
  const audPrice = accommodationPrices?.find((price) => price.currency_code === 'AUD');
  const cogsAud = audPrice?.cost_aud ?? null;
  const luxPlusPriceAud = audPrice?.lux_plus_price ?? null;
  // The package's ORIGINAL trading margin (svc-offer's `margin_percentage`, addon-aware) — shown
  // beside the Société reward's own computed margin, both in the summary and the override dialog.
  const marginPercentage = pkg.margin_percentage ?? null;

  return (
    <Stack spacing={1}>
      <SocieteRewardSummary
        reward={reward?.societe_reward ?? null}
        prices={reward?.prices ?? []}
        marginPercentage={marginPercentage}
        cogsAud={cogsAud}
        luxPlusPriceAud={luxPlusPriceAud}
        minMarginPercent={flashConfig?.minMarginPercent ?? null}
        vppFloorCents={flashConfig?.vppFloorCents ?? null}
        vppCeilingCents={flashConfig?.vppCeilingCents ?? null}
      />
      {configState.status === 'error' && (
        <Alert severity="error">{configState.message} Margin and VPP warnings are off until it loads.</Alert>
      )}
      {reward && canManage && (
        <Box>
          <Button size="small" variant="outlined" onClick={() => setDialogOpen(true)}>
            Manage
          </Button>
          <SocieteRewardOverrideDialog
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
            offerSfId={offerSfId}
            packageId={pkg.le_package_id}
            reward={reward.societe_reward}
            prices={reward.prices}
            sellingCurrencies={sellingCurrencies}
            cogsAud={cogsAud}
            luxPlusPriceAud={luxPlusPriceAud}
            marginPercentage={marginPercentage}
            minMarginPercent={flashConfig?.minMarginPercent ?? null}
            vppFloorCents={flashConfig?.vppFloorCents ?? null}
            vppCeilingCents={flashConfig?.vppCeilingCents ?? null}
            onApplied={(fresh) => setReward(fresh)}
          />
        </Box>
      )}
    </Stack>
  );
}
